import {AfterViewInit, Component, inject, OnInit, ViewChild} from '@angular/core';
import {MatTable, MatTableDataSource, MatTableModule} from "@angular/material/table";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatSort, MatSortModule} from "@angular/material/sort";
import {HttpClient} from "@angular/common/http";
import {MatInput, MatInputModule} from "@angular/material/input";
import {ActivatedRoute, Router} from "@angular/router";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {SelectionModel} from "@angular/cdk/collections";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {firstValueFrom, Observable} from "rxjs";
import {environment} from "../../../environments/environment";
import {VoiceMacro} from '@allaihealth/util';
import {MatDialog} from "@angular/material/dialog";
import {PageLoaderService} from "../../services/page-loader.service";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {YesNoData, YesNoDialogComponent} from "../../components/yes-no-dialog/yes-no-dialog.component";
import {FormsModule} from "@angular/forms";
import {MacroDialogComponent, VoiceMacroData} from "../../components/macro-dialog/macro-dialog.component";
import {SnackbarService} from "../../services/snackbar.service";
import {PostVisitCategories} from "@allaihealth/notes";
import {SessionService} from "../../services/session.service";

/** Display a list of recordings in the database */
@Component({
	selector: 'app-voice-macro-list',
	imports: [
		MatTableModule,
		MatFormFieldModule,
		MatSortModule,
		MatInputModule,
		MatCheckboxModule,
		MatIconModule,
		MatButtonModule,
		MatTooltipModule,
		MatPaginatorModule,
		FormsModule
	],
	templateUrl: './voice-macro-list.component.html',
	styleUrl: './voice-macro-list.component.scss'
})
export class VoiceMacroListComponent implements AfterViewInit, OnInit {
	/** Columns in the table */
	readonly displayedColumns:(keyof VoiceMacro | 'actions')[] =
		['phrase', 'section', 'substitution', 'actions'];

	/** Data source for the table */
	readonly dataSource:MatTableDataSource<VoiceMacro> = new MatTableDataSource<VoiceMacro>();

	/** Items selected in the table */
	selection:SelectionModel<VoiceMacro> = new SelectionModel(true, []);

	/** Header sort functions */
	@ViewChild(MatSort) sort:MatSort;

	/** Table controller */
	@ViewChild(MatTable) table:MatTable<VoiceMacro>;

	@ViewChild(MatPaginator)
	paginator:MatPaginator;

	@ViewChild(MatInput)
	searchInput!:MatInput;

	private sectionsNamesMap:Map<string, string>;


	constructor(private http:HttpClient,
	            private dialog:MatDialog,
	            private loader:PageLoaderService,
	            private router:Router,
	            private route:ActivatedRoute,
	            private snackbar:SnackbarService,
						  private sessionService:SessionService) {
	}

	ngOnInit() {
		//set the data for the list
		this.dataSource.data = this.route.snapshot.data["macros"];
		this.selection.clear();
		this.table?.renderRows();
		this.sectionsNamesMap = new Map(PostVisitCategories.map(section => [section.shortName, section.name]));

	}

	ngAfterViewInit() {
		this.dataSource.sort = this.sort;
		this.dataSource.paginator = this.paginator;
		this.table?.renderRows();
	}

	/** Apply a filter from the search bar */
	applyFilter(event?:Event) {
		if(!event) {
			this.searchInput.value = '';
			this.dataSource.filter = '';
		} else {
			const filterValue = (event.target as HTMLInputElement).value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}

		if(this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	/** Open a recording that was clicked on */
	goto(row:VoiceMacro) {
		this.router.navigate(['/', 'macros', row.id]).then();
	}

	/** Resolver to load recordings from the database */
	static resolve():Observable<VoiceMacro[]> {
		const http = inject(HttpClient);

		//get it from the server
		return http.get<VoiceMacro[]>(`${environment.apiUrl}/macros/list`);
	}

	protected async duplicateMacro(row:VoiceMacro) {
		this.loader.show("Duplicating voice macro...");
		try {
			const duplicateMacro = {
				id: '',
				organizationId: row.organizationId,
				providerId: row.providerId,
				phrase: `${row.phrase} - Copy`,
				substitution: row.substitution,
				section: row.section,
			}
			const newMacro = await firstValueFrom(this.http.post<VoiceMacro>(`${environment.apiUrl}/macros`, duplicateMacro));
			const newData = [newMacro];
			newData.push(...this.dataSource.data);
			this.dataSource.data = newData;
			this.table.renderRows();
			this.snackbar.error("Your macro has been duplicated.");
		} catch(error) {
			console.error(error);
			this.snackbar.error("Error saving macro.");
		} finally {
			this.loader.close();
		}
	}

	protected async createNew() {
		const original:VoiceMacro = {
			id: "",
				organizationId: "",
				providerId: "",
				phrase: "",
				substitution: "",
				section: ""
		};

		const macro = this.cloneMacro(original);

		const value = await firstValueFrom(
			this.dialog.open<MacroDialogComponent, VoiceMacroData, VoiceMacro>(
				MacroDialogComponent, {
					data: {
						title: "Create new macro",
						ok: "Save",
						cancel: "Cancel",
						macro,
						original
					}
				}).afterClosed());

		if(value) {
			this.loader.show("Creating voice macro...");
			try {
				const newMacro = await firstValueFrom(this.http.post<VoiceMacro>(`${environment.apiUrl}/macros`, value));
				const newData = [newMacro];
				newData.push(...this.dataSource.data);
				this.dataSource.data = newData;
				this.table.renderRows();
				this.snackbar.error("Your macro has been saved.");
			} catch(error) {
				console.error(error);
				this.snackbar.error("Error saving macro ");
			} finally {
				this.loader.close();
			}
		}
	}

	protected cloneMacro(macro:VoiceMacro) {
		return {
			id: macro.id,
			organizationId: macro.organizationId,
			providerId: macro.providerId,
			phrase: macro.phrase,
			substitution: macro.substitution,
			section: macro.section,
		}
	}

	protected async editMacro(row:VoiceMacro) {
		const value = await firstValueFrom(
			this.dialog.open<MacroDialogComponent, VoiceMacroData, VoiceMacro>(
				MacroDialogComponent, {
					data: {
						title: "Edit existing macro",
						ok: "Save",
						cancel: "Cancel",
						macro: this.cloneMacro(row),
						original: row
					}
				}).afterClosed());
		console.log(value);
		if(value) {
			try {
				this.loader.show("Updating macro...");
				row.phrase = value.phrase;
				row.section = value.section;
				row.substitution = value.substitution;
				await firstValueFrom(this.http.put<VoiceMacro>(`${environment.apiUrl}/macros/${row.id}`, row));
				this.table.renderRows();
				this.snackbar.error("Your macro has been saved.");
			} catch(error) {
				this.snackbar.error("Error saving macro.");
			} finally {
				this.loader.close();
			}
		}
	}

	protected async deleteMacro(row:VoiceMacro) {
		const value = await firstValueFrom(
			this.dialog.open<YesNoDialogComponent, YesNoData, boolean>(
				YesNoDialogComponent, {
					data: {
						yes: "Delete",
						no: "Cancel",
						title: "Delete macro?",
						message: "This will permanently delete the macro. Notes where it was already used will remain unchanged.",
						disableClose: false
					}
				}).afterClosed());

		if(value) {
			this.loader.showAndListen(this.http.delete(`${environment.apiUrl}/macros/${row.id}`), "Deleting...");
			const data = this.dataSource.data;
			const index = data.indexOf(row);
			data.splice(index, 1);
			this.dataSource.data = data;
			this.table.renderRows();
			this.snackbar.error("Your macro has been deleted.");
		}
	}

	protected getSectionName(section:string) {
		return this.sectionsNamesMap.get(section) || section;
	}

	/** Is the field disabled? */
	get disabled():boolean {
		return this.dataSource.data.length >= this.sessionService.allaiUser.voice_macro_limit;
	}
}
